<template>
  <div class="tempus-iframe-800-container tempus-iframe-form" v-show="paymentDivVisible" ref="paymentCardInput" id="payment_card_input">
    <div class="container py-4">
      <h2 class="heading" :id="$id('payLabel')">{{ location.brand }} Payment</h2>
      <p>Amount to pay: <strong v-if="sale">{{ amountToPay | money }}</strong></p>

      <div class="form-group">
        <label :for="$id('cardholder-name')">Name On Card:</label>
        <input
          v-model="cardholderName"
          :id="$id('cardholder-name')"
          :class="['form-control', nameError ? 'is-invalid' : '']"
          type="text"
          placeholder="Card holder name"
          autocomplete="cc-name"
          :disabled="cardButtonDisable"
          @input="updateName">
        <div v-if="nameError" class="invalid-feedback" role="alert">{{ nameError }}</div>
      </div>

      <div class="form-group">

        <div class="custom-control custom-switch custom-switch-adaptive mb-2"
          :class="[cardError ? 'is-invalid' : '']">
          <input :id="$id('card-pass')" type="checkbox" class="custom-control-input" v-model="cardPass">
          <label :for="$id('card-pass')" class="custom-control-label">{{ cardPass ? 'Card Pass' : 'Card Fail' }}</label>
        </div>

        <div v-if="cardError" class="invalid-feedback" role="alert">{{ cardError }}</div>
      </div>

      <button
        type="submit"
        class="btn btn-primary btn-lg btn-block"
        :disabled="cardButtonDisable"
        @click="submitPayment">
        <span class="material-icons" aria-hidden="true" style="position: relative; top: 5px;">credit_card</span> Submit Payment Of {{ amountToPay | money }}
      </button>

      <div class="mt-4 alert alert-yellow text-tempus-blue-600" v-if="(timeLeftToPay && timeLeftToPay.asMinutes() < 60) || timeExpired">
        <span class="material-icons md-72 pr-4">warning</span>
        <template v-if="timeLeftToPay && timeLeftToPay.asMinutes() < 60">
          <p class="mb-2">Please complete your payment in the next <strong class="text-monospace">{{ timeLeftToPayFormatted }}</strong></p>
          <p class="mb-0 text-dark-red font-italic">Failure to pay on time will release slots back to general sale.</p>
        </template>
        <template v-if="timeExpired">
          <p class="mb-2 text-dark-red font-weight-bold">
            Sorry you did not complete your payment in time.
          </p>
        </template>
      </div>

      <div class="row" v-show="showWaitingAlert" ref="waitingAlertWrapper" id="waiting_alert_wrapper">
        <div class="col">
          <div class="alert alert-yellow">
            <span class="material-icons md-72 pr-4 text-pink">warning</span>
            <h4 class="alert-heading heading">Communicating With Payment Provider - Please Wait</h4>
            <p>We are now communicating with our payment provider, backend processes are being completed.</p>
            <p class="font-weight-bold text-pink">Do not click back or refresh your browser, this should not take much time.</p>
            <img class="mx-auto d-block" src="/img/ajax_lg.gif" alt="Awaiting server to retrieve the information">
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  props: {
    consoleDebug: {
      type: Boolean,
      default: false,
    },
    location: {
      type: Object,
      required: true,
    },

    paymentProviderPublicOptions: {
      type: [Array, Object],
      default: null,
    },
  },

  data() {
    return {
      sale: null,
      saleable: null,
      payingDeposit: false,

      cardholderName: '',
      cardPass: true,
      cardButtonDisable: true,

      amount: null,
      intentId: null,
      paymentExpiry: null,

      isLoading: true,
      loader: null,
      cardError: null,
      nameError: null,
      successMessage: 'Payment successful.',
      // reloadTime: 2,

      currentTimeInterval: null,
      currentTime: moment(),

      paymentDivVisible: false,
      showWaitingAlert: false,

      vouchersToRedeem: [],
    };
  },

  computed: {
    totalOfVoucherRedemption() {
      if (this.vouchersToRedeem.length) {
        return parseInt(this.vouchersToRedeem.reduce((partialSum, v) => partialSum + parseInt(v.cashValue), 0));
      }
      return 0;
    },

    amountToPay() {
      if (this.vouchersToRedeem && this.vouchersToRedeem.length) {
        return parseInt(this.sale.left_to_pay) - parseInt(this.totalOfVoucherRedemption);
      }

      if (this.amount) {
        return this.amount;
      }

      if (this.sale == null) {
        return 0;
      }

      if (this.payingDeposit) {
        if (this.sale.deposit_amount) {
          return this.sale.deposit_amount;
        }

        return this.sale.sale_items.filter(si => si.saleable_type == 'App\\Models\\Booking').length * 5000;
      }

      return this.sale.left_to_pay;
    },

    timeLeftToPay() {
      if (this.paymentExpiry == null) {
        return null;
      }

      let duration = moment.duration(moment(this.paymentExpiry).subtract(60, 'seconds').diff(this.currentTime));

      return duration;
    },

    timeLeftToPayFormatted() {
      if (this.timeLeftToPay == null) {
        return null;
      }
      if (this.timeLeftToPay < 0) {
        return "00 minutes 00 seconds";
      }

      return this.timeLeftToPay.format('mm [minutes] ss [seconds]', {
        trim: false
      });
    },

    timeExpired() {
      if (this.timeLeftToPay) {
        return this.timeLeftToPay < 0;
      }

      return false;
    },
  },

  watch: {
    sale(newSale, oldSale) {
      if (newSale && newSale.left_to_pay) {
        if (oldSale == null) {
          this.echoInitSale(newSale);
        } else if (oldSale.id != newSale.id) {
          this.echoDeInitSale(oldSale);
          this.echoInitSale(newSale);
        }
        this.createIntent();
      } else {
        this.amount = null;
        this.intentId = null;
        this.paymentExpiry = null;
        this.cardholderName = '';
        this.payingDeposit = false;
        this.echoDeInitSale(oldSale);
      }
    },

    timeExpired(newTimeExpired) {
      if (newTimeExpired) {
        // disable buttons
        this.cardButtonDisable = true;
        this.$emit('payment-time-expired')
      } else {
        // enable buttons
        this.cardButtonDisable = false;
      }
    },

    cardPass() {
      this.cardError = null;
    },
  },

  methods: {
    /* ===============
     * public methods
     */

    saleRefreshed(sale) {
      if (this.consoleDebug) console.log('PayTestingIframe::saleRefreshed', sale);
      this.sale = sale;
    },

    addVouchersToSale(vouchersToRedeem) {
      if (this.consoleDebug) console.log('PayTestingIframe::addVouchersToSale', vouchersToRedeem);
      this.vouchersToRedeem = vouchersToRedeem;
      this.createIntent();
    },

    takeSalePayment(sale, voucherDiscountObject, allowDepositFlag = false) {
      if (this.consoleDebug) console.log('PayTestingIframe::takeSalePayment', sale, allowDepositFlag)
      if (voucherDiscountObject && typeof(voucherDiscountObject.vouchersToSpend) != 'undefined') {
        this.vouchersToRedeem = voucherDiscountObject.vouchersToSpend;
      }
      this.payingDeposit = allowDepositFlag;
      this.sale = sale;

      this.showTestingDiv();
    },

    /*
     * END public methods
     * =============== */

    /* ===============
     * paymentCardInput methods
     */

    showTestingDiv() {
      // Do any prep before showing the modal
      this.cardholderName = this.sale.customer.name;

      this.paymentDivVisible = true;

      this.$emit('scroll-payment-div-into-view');
      // this.$nextTick(() => {
      //   document.getElementById('payment_card_input').scrollIntoView({ behavior: 'smooth', block: 'start',});
      // });
    },

    async reloadPageTimer(sale) {
      if (this.consoleDebug) console.log('PayTestingIframe::reloadPageTimer (async start)');

      this.loading(true);
      await this.completePostPaymentProcesses();
      if (this.consoleDebug) console.log('PayTestingIframe::reloadPageTimer (await over)');
      this.loading(false);
      if (this.consoleDebug) console.log('PayTestingIframe::reloadPageTimer emitting payment-reload-timer-completed', this.amount);
      this.$emit('payment-reload-timer-completed', this.amount);
      return;

      if (this.consoleDebug) console.log('PayTestingIframe::reloadPageTimer', this.reloadTime, sale);
      if (this.reloadTime <= 0) {
        var href = this.route('location.payment.success', [this.location, sale]);
        if (this.consoleDebug) console.log('reloadPageTimer', href);
        this.completePostPaymentProcesses();
        this.$emit('payment-reload-timer-completed', sale);
        // window.location.href = this.route('location.payment.success', [this.location, sale]); // redirect to .... payment success page
      } else {
        setTimeout(() => {
          this.reloadTime -= 1;
          this.reloadPageTimer(sale);
        }, 1000);
      }
    },

    completePostPaymentProcesses() {
      if (this.consoleDebug) console.log('PayTestingIframe::completePostPaymentProcesses (start)');

      return new Promise(resolve => {
        if (this.vouchersToRedeem.length == 0) {
          resolve(true);
          return true;
        }

        var voucherTransactions = [];
        this.vouchersToRedeem.forEach(voucher => {
          voucherTransactions.push({
            amount: voucher.cashValue,
            booked_by: 'W',
            cart_reference: voucher.reference,  // don't change this or everything will break
            from: 'C', // checkout
            transaction_reference: null,
            transaction_type_id: 15,
          });
        });

        let request = {
          transactions: voucherTransactions,
        };

        axios.post(this.route('api.sales.transactions.store', this.sale.hashid), request)
          .then((response) => {
            if (response.status == 200) {
              if (this.consoleDebug) console.log('PayTestingIframe::completePostPaymentProcesses resolving');

              this.paymentDivVisible = false;
              resolve(true);
              return true;

            } else {
              flash('Error submitting new Transactions', 'danger');
              console.log('takePaymentSale::voucher', response.data);
              console.log('takePaymentSale::voucher', response.status);
              console.log('takePaymentSale::voucher', response.statusText);
            }

            this.paymentDivVisible = false;
            resolve(false);
            return false;
          })
          .catch((error) => {
            flash('Error processing Voucher', 'danger');
            if (error.response) {
              // if HTTP_UNPROCESSABLE_ENTITY some validation error laravel or us
              if (error.response.status == 422) {
                console.log('takePaymentSale::voucher -  laravel validation error', error.response.data);
              }
              // else if HTTP_CONFLICT to many saleables or over lap
              else if (error.response.status == 409) {
                this.lockError = error.response.data;
              }
              // else if HTTP_FORBIDDEN on enough permissions
              else if (error.response.status == 401) {
                // else if HTTP_UNAUTHORIZED not authorized
                console.log('takePaymentSale::voucher - 401', error.response.data);
              }
              else {
                console.log('takePaymentSale::voucher - Response error', error.response.data, error.response.status, error.response.headers);
              }
            } else if (error.request) {
              // The request was made but no response was received
              // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
              // http.ClientRequest in node.js
              console.error('takePaymentSale::voucher - Request error', error.request);
            } else {
              // Something happened in setting up the request that triggered an Error
              console.error('takePaymentSale::voucher - Error', error.message);
            }
            this.paymentDivVisible = false;
            resolve(false);
            return false;
          });
      });
    },

    /*
     * END paymentCardInput methods
     * =============== */

    createIntent() {
      if (this.sale == null) {
        return;
      }

      this.amount = this.amountToPay;

      let intent = {
        locationId: this.location.id,
        saleId: this.sale.id,
        payDeposit: this.payingDeposit,
        vouchersToRedeem: this.vouchersToRedeem,
      };

      this.loading(true, this.$refs.paymentCardInput);

      axios.post(this.route('api.pay-testing.make-intent'), intent)
        .then((response) => {
          if (this.consoleDebug) console.log('PayTestingIframe::createIntent');
          if (this.consoleDebug) console.log('axios back: api.pay-testing.make-intent');
          if (this.consoleDebug) console.log(response);
          if (this.consoleDebug) console.log(response.data);
          if (response.status == '201') { // HTTP_CREATED
            // pull out pi and client secret
            //
            this.intentId = response.data.intentId;
            this.paymentExpiry = response.data.paymentExpiry;
            // enable buttons
            this.cardButtonDisable = false;
          } else {
            flash('Error setting up payment', 'danger');
            console.log('createIntent', response.data);
            console.log('createIntent', response.status);
            console.log('createIntent', response.statusText);
          }
          this.loading(false);
        })
        .catch((error) => {
          flash('Error setting up payment', 'danger');
          if (error.response) {
            // if HTTP_UNPROCESSABLE_ENTITY some validation error laravel or us
            // else if HTTP_CONFLICT
            // else if HTTP_FORBIDDEN on enough permissions
            console.log('createIntent: Response error', error.response.data, error.response.status, error.response.headers);
          } else if (error.request) {
            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
            // http.ClientRequest in node.js
            console.error('createIntent: Request error', error.request);
          } else {
            // Something happened in setting up the request that triggered an Error
            console.error('createIntent: Error', error.message);
          }

          // this.loading(false);
        });
    },

    updateName() {
      this.nameError = null;
    },

    scrollDomIdIntoView(id) {
      // testing is only on dev, do not need anything fancy
      this.$nextTick(() => {
        const elm = document.getElementById(id);
        const yOffset = -80; // tested for Cryptology because of the navbar, and only for 'move-to-player-entry' / 'chooseYourTeam'
        window.scrollTo({top: elm.getBoundingClientRect().top + window.scrollY + yOffset, behavior: 'smooth' });
      });
    },

    submitPayment() {
      // card input box
      if (! this.cardholderName) {
        this.nameError = 'Card holder name is required.';
        return;
      }

      // now good to proceed, so show signs of whirlygig

      this.showWaitingAlert = true;

      this.$nextTick(() => {
        this.scrollDomIdIntoView('waiting_alert_wrapper');
        // document.getElementById('waiting_alert_wrapper').scrollIntoView({ behavior: 'smooth', block: 'start',});
      });

      this.loading(true, this.$refs.paymentCardInput);
      this.cardButtonDisable = true;
      this.cardError = null;

      if (this.cardPass == false) {
        // Display error.message in your UI.
        flash('Payment error: Set to Fail', 'warning');
        this.cardError = 'Card set to Fail';
        this.cardButtonDisable = false;
        this.loading(false);
      } else {
        // The payment has succeeded. Display a success message.
        this.loading(false);
        this.paymentSuccess();
      }
    },

    paymentSuccess() {
      if (this.consoleDebug) console.log('PayTestingIframe::paymentSuccess');

      // let backend know?
      let intent = {
        locationId: this.location.id,
        saleId: this.sale.id,
        intentId: this.intentId,
      };

      this.loading(true, this.$refs.paymentCardInput);

      axios.post(this.route('api.pay-testing.intent-success'), intent)
        .then((response) => {
          if (response.status == '202') { // HTTP_ACCEPTED
            if (this.consoleDebug) console.log('PayTestingIframe::paymentSuccess - accepted');
          } else if (response.status == '204') { // HTTP_NO_CONTENT
            if (this.consoleDebug) console.log('PayTestingIframe::paymentSuccess - confirmed');
          } else {
            flash('PayTestingIframe::paymentSuccess - Error reporting payment success', 'danger');
            console.log('paymentSuccess', response.data);
            console.log('paymentSuccess', response.status);
            console.log('paymentSuccess', response.statusText);
          }

          this.loading(false);

          setTimeout(() => {
            this.paymentSuccessConfirmed();
          }, 2000);
        })
        .catch((error) => {
          flash('Error reporting payment success', 'danger');
          if (error.response) {
            // if HTTP_UNPROCESSABLE_ENTITY some validation error laravel or us
            // else if HTTP_CONFLICT
            // else if HTTP_FORBIDDEN on enough permissions
            console.log('paymentSuccess: Response error', error.response.data, error.response.status, error.response.headers);
          } else if (error.request) {
            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
            // http.ClientRequest in node.js
            console.error('paymentSuccess: Request error', error.request);
          } else {
            // Something happened in setting up the request that triggered an Error
            console.error('paymentSuccess: Error', error.message);
          }

          this.loading(false);
          this.paymentSuccessError();
        });
    },

    paymentSuccessConfirmed() {
      // this is fired after the payment intent has come back
      // background processes are running, but the sale nor transaction are returned

      this.reloadPageTimer(Vue.util.extend({}, JSON.parse(JSON.stringify(this.sale))));
    },

    paymentSuccessError() {
      if (this.consoleDebug) console.log('PayTestingIframe::paymentSuccessError')
      // swap modal
      // this.completePostPaymentProcesses();
      // $(this.$refs.errorModal).modal('show');
    },

    /* ===============
     * Echo methods
     */

    echoInitSale(sale) {
      if (! process.env.MIX_PUSHER_APP_KEY) {
        return;
      }

      if (sale == null) {
        return;
      }

      Echo.channel('sale.' + sale.hashid)
        .listen('Sale.SaleUpdated', this.saleUpdatedEvent)
        .listen('Sale.SaleDeleted', this.saleDeletedEvent);
    },

    echoDeInitSale(sale) {
      if (! process.env.MIX_PUSHER_APP_KEY) {
        return;
      }

      if (sale == null) {
        return;
      }

      Echo.leave('sale.' + sale.hashid);
    },

    saleUpdatedEvent(event) {
      console.log('saleUpdatedEvent(event)', event);
    },

    saleDeletedEvent(event) {
      console.log('saleDeletedEvent(event)', event);
    },

    /*
     * END Echo methods
     * =============== */
  },

  mounted() {
    this.currentTimeInterval = setInterval(() => {
      this.currentTime = moment(); // TODO: make as server timezone / same zone as paymentExpiry
    }, 100);
  },

  beforeDestroy() {
    clearInterval(this.currentTimeInterval);
  },
};
</script>


<style lang="scss" scoped>
/*
 * PayTesting.vue
 */

.custom-switch.custom-switch-adaptive {
  padding-left: 0;

  .custom-control-label {
    padding: 0 1.5em;
    position: relative;
    border-radius: 1em;
    line-height: 1.4em;
    color: var(--gray);
    border: 1px solid var(--gray);
    background-color: var(--light);
    transition: background-color 200ms;

    &::before {
      content: none;
    }
    &::after {
      position: absolute;
      height: 1em;
      top: 0.2em;
      left: 0.2em;
      width: 1em;
      border-radius: 1em;

      transition: left 200ms;
    }
  }

  .custom-control-input:checked ~ .custom-control-label {
    color: var(--white);
    background-color: var(--success);
    border-color: var(--success);

    &::after {
      background-color: var(--white);
      left: calc(100% - 1.2em);

      transform: none;
      -webkit-transform: none;
    }
  }
}

</style>
